import { FC, useCallback, useState } from 'react';
import { useFragment } from 'react-relay';
import { Form, Formik } from 'formik';
import graphql from 'babel-plugin-relay/macro';
import * as yup from 'yup';

import SearchIcon from '@mui/icons-material/Search';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import LoadingButton from '@mui/lab/LoadingButton';
import MuiCheckbox from '@mui/material/Checkbox';
import TwoColumn from 'components/TwoColumn';
import AirportSelect, { makeOption, Option } from 'components/AirportSelect';
import DatePicker from 'components/DatePicker';
import TextField from 'components/TextField';
import FormAutosubmit from 'components/FormAutosubmit';
import Checkbox from 'components/Checkbox';

import { dateFieldValue } from 'lib';
import { FlightSearchForm_data$key } from '__generated__/FlightSearchForm_data.graphql';
import { FlightSearchForm_defaultDepartureAirport$key } from '__generated__/FlightSearchForm_defaultDepartureAirport.graphql';
import { FlightSearchForm_defaultArrivalAirport$key } from '__generated__/FlightSearchForm_defaultArrivalAirport.graphql';

export type FormFields = {
  directOnly: boolean;
  departureAirport: Option | null;
  arrivalAirport: Option | null;
  departureDate: Date | null;
  returnDate: Date | null;
  adults: string;
  children: string;
  infant: string;
};

type Props = {
  data: FlightSearchForm_data$key;
  defaultDepartureAirport: FlightSearchForm_defaultDepartureAirport$key | null;
  defaultArrivalAirport: FlightSearchForm_defaultArrivalAirport$key | null;
  loading?: boolean;
  departureDate?: string | null;
  returnDate?: string | null;
  onSubmit: (values: FormFields) => void;
  autoSubmit?: boolean;
};

const schema = yup.object({
  departureAirport: yup
    .object()
    .nullable()
    .required('Prašome pasirinkti išvykimo oro uostą'),
  arrivalAirport: yup
    .object()
    .nullable()
    .required('Prašome pasirinkti išvykimo oro uostą'),
  departureDate: yup.date().nullable().required('Prašome įvesti išvykimo datą'),
  returnDate: yup.date().nullable().required('Prašome įvesti grįžimo datą'),
  adults: yup.string().nullable().required('Prašome keleivių skaičių'),
});

const FlightSearchForm: FC<Props> = props => {
  const {
    loading,
    departureDate,
    returnDate,
    onSubmit,
    autoSubmit: autosubmit,
  } = props;
  const [hasReturn, setHasReturn] = useState(true);

  const data = useFragment(
    graphql`
      fragment FlightSearchForm_data on Query {
        ...AirportSelect_data
      }
    `,
    props.data
  );

  const defaultDepartureAirport = useFragment(
    graphql`
      fragment FlightSearchForm_defaultDepartureAirport on Airport {
        id
        name
        code
      }
    `,
    props.defaultDepartureAirport
  );

  const defaultArrivalAirport = useFragment(
    graphql`
      fragment FlightSearchForm_defaultArrivalAirport on Airport {
        id
        name
        code
      }
    `,
    props.defaultArrivalAirport
  );

  const handleHasReturnCLick = useCallback(() => {
    setHasReturn(v => !v);
  }, []);

  const initial = {
    directOnly: true,
    departureAirport:
      defaultDepartureAirport && makeOption(defaultDepartureAirport),
    arrivalAirport: defaultArrivalAirport && makeOption(defaultArrivalAirport),
    departureDate: dateFieldValue(departureDate ?? null),
    returnDate: dateFieldValue(returnDate ?? null),
    adults: '2',
    children: '0',
    infant: '0',
  };

  return (
    <Formik<FormFields>
      onSubmit={onSubmit}
      validationSchema={schema}
      initialValues={initial}>
      <Form>
        <FormAutosubmit enabled={autosubmit} />
        <Stack spacing={4}>
          <TwoColumn
            left={
              <FormGroup>
                <FormControlLabel
                  control={
                    <MuiCheckbox
                      defaultChecked
                      onChange={handleHasReturnCLick}
                    />
                  }
                  label="Skrydis atgal"
                />
              </FormGroup>
            }
            right={
              <FormGroup>
                <FormControlLabel
                  control={<Checkbox name="directOnly" />}
                  label="Tik tiesioginiai"
                />
              </FormGroup>
            }
          />

          <TwoColumn
            left={
              <AirportSelect
                fullWidth
                name="departureAirport"
                label="Iš kur"
                data={data}
              />
            }
            right={
              <AirportSelect
                fullWidth
                name="arrivalAirport"
                label="Į kur"
                data={data}
              />
            }
          />

          <TwoColumn
            left={
              <DatePicker fullWidth name="departureDate" label="Išvykimas" />
            }
            right={
              hasReturn && (
                <DatePicker fullWidth name="returnDate" label="Grįžimas" />
              )
            }
          />

          <TwoColumn
            left={
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                <TextField
                  helperText="12+ m."
                  variant="standard"
                  label="Suaugę"
                  name="adults"
                />

                <TextField
                  helperText="2-11 m."
                  variant="standard"
                  label="Vaikai"
                  name="children"
                />

                <TextField
                  helperText="0-1 m."
                  variant="standard"
                  label="Kūdikiai"
                  name="infant"
                />
              </Box>
            }
            right={
              <LoadingButton
                fullWidth
                type="submit"
                variant="outlined"
                loading={loading}
                startIcon={<SearchIcon />}>
                Ieškoti skrydžių
              </LoadingButton>
            }
          />
        </Stack>
      </Form>
    </Formik>
  );
};

export default FlightSearchForm;
